import { useState, useEffect } from "react";
import "@/index.css";
import { LangProvider } from "./lib/i18n";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import UpgradePage from "./pages/UpgradePage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import BillingReturnPage from "./pages/BillingReturnPage";
import FaqPage from "./pages/FaqPage";
import { Toaster } from "./components/ui/sonner";
import InstallPrompt from "./components/InstallPrompt";
import { useSeo } from "./lib/seo";

function pageFromLocation() {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  if (path === "/privacy") return "privacy";
  if (path === "/terms") return "terms";
  if (path === "/faq") return "faq";
  return new URLSearchParams(window.location.search).has("billing_return") ? "billing-return" : "dashboard";
}

function pagePath(page) {
  if (page === "privacy") return "/privacy";
  if (page === "terms") return "/terms";
  if (page === "faq") return "/faq";
  return "/";
}

function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState(pageFromLocation);
  useSeo(user ? "login" : page === "dashboard" ? "login" : page);

  const navigate = (nextPage) => {
    const nextPath = pagePath(nextPage);
    if (window.location.pathname !== nextPath) window.history.pushState({}, "", nextPath);
    setPage(nextPage);
  };

  useEffect(() => {
    const token = localStorage.getItem("pm_token");
    const stored = localStorage.getItem("pm_user");
    if (token && stored) {
      try { setUser(JSON.parse(stored)); } catch { localStorage.clear(); }
    }
  }, []);

  useEffect(() => {
    const onPopState = () => setPage(pageFromLocation());
    window.__showPrivacy = () => navigate("privacy");
    window.__showTerms = () => navigate("terms");
    window.__showFaq = () => navigate("faq");
    window.addEventListener("popstate", onPopState);
    return () => {
      delete window.__showPrivacy;
      delete window.__showTerms;
      delete window.__showFaq;
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const handleLogin = (u) => { setUser(u); navigate("dashboard"); };
  const handleLogout = () => {
    localStorage.removeItem("pm_token");
    localStorage.removeItem("pm_user");
    localStorage.removeItem("pm_cache_passwords");
    setUser(null);
    navigate("dashboard");
  };

  if (page === "privacy") return <LangProvider><PrivacyPage onBack={() => navigate(user ? "dashboard" : "login")} /><Toaster position="bottom-right" richColors /></LangProvider>;
  if (page === "terms") return <LangProvider><TermsPage onBack={() => navigate(user ? "dashboard" : "login")} /><Toaster position="bottom-right" richColors /></LangProvider>;
  if (page === "faq") return <LangProvider><FaqPage onBack={() => navigate(user ? "dashboard" : "login")} /><Toaster position="bottom-right" richColors /></LangProvider>;

  return (
    <LangProvider>
      {!user ? (
        <LoginPage onLogin={handleLogin} onPrivacy={() => navigate("privacy")} onTerms={() => navigate("terms")} onFaq={() => navigate("faq")} />
      ) : page === "admin" && user.role === "admin" ? (
        <AdminPage user={user} onBack={() => setPage("dashboard")} onLogout={handleLogout} />
      ) : page === "upgrade" ? (
        <UpgradePage user={user} onBack={() => setPage("dashboard")} onPremiumActivated={() => setUser({ ...user, plan: "premium" })} />
      ) : page === "billing-return" ? (
        <BillingReturnPage onBack={() => setPage("dashboard")} />
      ) : (
        <DashboardPage
          user={user}
          setUser={setUser}
          onLogout={handleLogout}
          onAdmin={() => setPage("admin")}
          onUpgrade={() => setPage("upgrade")}
        />
      )}
      <Toaster position="bottom-right" richColors />
      <InstallPrompt />
    </LangProvider>
  );
}

export default App;
